import { Injectable } from "@angular/core";
import { combineLatest, map, Observable } from "rxjs";
import { LocationStateQuery } from "../state/location-state.query";
import { OperationalDispatchUnit } from "../state/location-state.store";
import { UiQuery } from "../state/ui.query";
import { UiService } from "../state/ui.service";
import { FeatureFlagService } from "source/app/features/feature-flag/services/feature-flag.service";
import { RIDEOPS_ASSET_HIERARCHY_PROTOTYPE } from "source/app/features/feature-flag/feature-flag.constants";

const defaultUnitOrder = (units: OperationalDispatchUnit[]): OperationalDispatchUnit[] =>
	[...units].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

/** Applies a device-local order override, appending any units missing from the override by server default. */
export const applyUnitOrderOverride = (
	units: OperationalDispatchUnit[],
	override: string[] | null,
): OperationalDispatchUnit[] => {
	if (!override?.length) {
		return defaultUnitOrder(units);
	}

	const byId = new Map(units.map((unit) => [unit.id, unit]));
	const ordered = override.map((id) => byId.get(id)).filter((unit): unit is OperationalDispatchUnit => !!unit);
	const overrideIds = new Set(override);
	const remainder = defaultUnitOrder(units.filter((unit) => !overrideIds.has(unit.id)));

	return [...ordered, ...remainder];
};

/**
 * Centralises asset-based dispatch behaviour: the gate chain (assetHierarchyPrototype +
 * asset-based-dispatch config + riders/dispatch widget visibility) and the ordered
 * unit / current-unit selection used by the queue widget and dispatch wiring.
 */
@Injectable({ providedIn: "root" })
export class AssetDispatchService {
	constructor(
		private locationStateQuery: LocationStateQuery,
		private uiQuery: UiQuery,
		private uiService: UiService,
		private featureFlagService: FeatureFlagService,
	) {
		this.persistFlagAvailabilityWhileSignedIn();
	}

	/**
	 * Live CORE `rideops__assetHierarchyPrototype` when an operator is signed in and flags are
	 * loaded; otherwise the last value persisted to the UI store. Flags are cleared on failed
	 * refresh (offline), so without this cache asset-mode locations would drop to location-dispatch UI.
	 */
	readonly assetHierarchyPrototypeFlagEnabled$: Observable<boolean> = combineLatest([
		this.featureFlagService.flags$,
		this.locationStateQuery.selectOperator$,
		this.uiQuery.assetHierarchyPrototypeFlagAvailable$,
	]).pipe(
		map(([flags, operator, lastKnownAvailable]) => {
			if (operator == null || flags.length === 0) {
				return lastKnownAvailable;
			}
			return this.featureFlagService.isEnabled(RIDEOPS_ASSET_HIERARCHY_PROTOTYPE);
		}),
	);

	private ridersWidgetVisible$ = this.uiQuery.widgetOptions$.pipe(map((options) => options.riders.visible));

	// Units ordered for the dispatch queue: server sortOrder by default, or a device-local override
	// for this location after the operator reorders the unit list.
	readonly sortedUnits$: Observable<OperationalDispatchUnit[]> = combineLatest([
		this.locationStateQuery.assetUnits$,
		this.locationStateQuery.assetUnitOrderOverride$,
	]).pipe(map(([units, override]) => applyUnitOrderOverride(units, override)));

	// Units still available for the rotating dispatch queue (device-local unavailable units excluded).
	readonly availableSortedUnits$: Observable<OperationalDispatchUnit[]> = combineLatest([
		this.sortedUnits$,
		this.uiQuery.unavailableUnitIds$,
	]).pipe(
		map(([units, unavailableUnitIds]) => {
			const unavailable = new Set(unavailableUnitIds);
			return units.filter((unit) => !unavailable.has(unit.id));
		}),
	);

	// True when the location is configured for asset-based dispatch and assetHierarchyPrototype is on.
	// Unlike `assetDispatchActive$`, this ignores riders-widget visibility so stats / layout can
	// still key off asset mode when the dispatch card is hidden.
	readonly assetBasedDispatchEnabled$: Observable<boolean> = combineLatest([
		this.assetHierarchyPrototypeFlagEnabled$,
		this.locationStateQuery.useAssetBasedDispatchConfiguration$,
	]).pipe(map(([flagEnabled, assetBased]) => flagEnabled && assetBased));

	isAssetBasedDispatchEnabled = (): boolean =>
		this.isAssetHierarchyPrototypeFlagEnabled() &&
		(this.locationStateQuery.getSnapshot().rideOpsSettings.useAssetBasedDispatchConfiguration ?? false);

	// True only when the operator-facing unit queue experience is active: assetHierarchyPrototype on,
	// the location uses asset-based dispatch configuration, and the shared riders/dispatch widget
	// visibility is on. Single gate for dispatch wiring and related widgets.
	readonly assetDispatchActive$: Observable<boolean> = combineLatest([
		this.assetBasedDispatchEnabled$,
		this.ridersWidgetVisible$,
	]).pipe(map(([assetBasedEnabled, ridersVisible]) => assetBasedEnabled && ridersVisible));

	// The unit currently "in action" (centre slot of the queue), resolved from the persisted
	// queue index modulo the available unit list so it stays valid if units were marked unavailable.
	readonly currentUnit$: Observable<OperationalDispatchUnit | null> = combineLatest([
		this.availableSortedUnits$,
		this.locationStateQuery.assetDispatchQueueIndex$,
	]).pipe(map(([units, index]) => this.resolveCurrentUnit(units, index)));

	isAssetDispatchActive = (): Promise<boolean> => this.assetDispatchActive$.firstAsync();

	getCurrentUnit = (): Promise<OperationalDispatchUnit | null> => this.currentUnit$.firstAsync();

	private isAssetHierarchyPrototypeFlagEnabled = (): boolean => {
		const operator = this.locationStateQuery.getSnapshot().operator;
		if (operator == null || this.featureFlagService.getFlags().length === 0) {
			return this.uiQuery.getAssetHierarchyPrototypeFlagAvailable();
		}
		return this.featureFlagService.isEnabled(RIDEOPS_ASSET_HIERARCHY_PROTOTYPE);
	};

	private persistFlagAvailabilityWhileSignedIn = () => {
		combineLatest([this.featureFlagService.flags$, this.locationStateQuery.selectOperator$]).subscribe(
			([flags, operator]) => {
				if (operator == null) {
					return;
				}
				// Skip the empty post-clear / pre-load window so we do not wipe a good cache.
				if (flags.length === 0) {
					return;
				}

				const available = this.featureFlagService.isEnabled(RIDEOPS_ASSET_HIERARCHY_PROTOTYPE);
				if (available === this.uiQuery.getAssetHierarchyPrototypeFlagAvailable()) {
					return;
				}
				this.uiService.setAssetHierarchyPrototypeFlagAvailable(available);
			},
		);
	};

	private resolveCurrentUnit = (units: OperationalDispatchUnit[], index: number): OperationalDispatchUnit | null => {
		const count = units.length;
		if (count === 0) {
			return null;
		}
		const safeIndex = (((index ?? 0) % count) + count) % count;
		return units[safeIndex] ?? null;
	};
}
